import { Button } from "@sellar-io/components";
import { OrderPaymentType } from "@sellar-io/api-v2/types";
import { ReactElement, useState } from "react";
import { SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useOrderCreateInvoice } from "../../../hooks/api/v2/order/useOrderCreateInvoice";
import { useOrderCreateBuyerInvoice } from "../../../hooks/api/v2/order/useOrderCreateBuyerInvoice";
import { FullDutyAlert } from "../Alerts/FullDutyAlert";
import { OrderPaymentMethodFormFragment } from "./OrderPaymentMethodFormFragment";

export const OrderRequestCardPaymentModal = ({
  onDismiss,
  active,
  buyerName,
  orderId,
  onSuccess,
  stripeAccountId,
  paymentType,
  hasNoDraughtDutyRelief,
}: {
  onDismiss: () => void;
  active: boolean;
  orderId: number;
  buyerName: string;
  onSuccess: () => void;
  stripeAccountId?: string | null;
  paymentType: OrderPaymentType | null;
  hasNoDraughtDutyRelief: boolean;
}): ReactElement => {
  // stripe hosted invoice

  const orderCreateInvoiceMutation = useOrderCreateInvoice(orderId);
  const orderCreateBuyerInvoiceMutation = useOrderCreateBuyerInvoice(orderId);
  const [paymentMethod, setPaymentMethod] = useState<OrderPaymentType | null>(
    paymentType,
  );
  const requestCardPayment = () => {
    if (paymentMethod === "stripe") {
      return orderCreateInvoiceMutation.mutate(undefined, {
        onSuccess: () => {
          onSuccess();
          onDismiss();
        },
      });
    }
    return orderCreateBuyerInvoiceMutation.mutate(
      {
        paymentType: paymentMethod,
      },
      {
        onSuccess: () => {
          onSuccess();
          onDismiss();
        },
      },
    );
  };
  const loading =
    orderCreateInvoiceMutation.isPending ||
    orderCreateBuyerInvoiceMutation.isPending;
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        onClickLeftIcon={onDismiss}
        leftIcon={"CHEVRON_DOWN"}
        // NOTE: this is a stripe hosted invoice, not proforma payment
        title="Request pro-forma payment"
        description={`You won't be able to edit this order once the invoice has been issued.`}
      />
      <DialogContent className={"p-4"}>
        {orderCreateInvoiceMutation.isError && (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(orderCreateInvoiceMutation.error)?.response?.data
                .message ?? "An unknown error occurred"
            }
          />
        )}
        {hasNoDraughtDutyRelief && (
          <FullDutyAlert buyerName={buyerName} className={"my-2"} />
        )}
        {orderCreateBuyerInvoiceMutation.isError && (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(orderCreateBuyerInvoiceMutation.error)?.response
                ?.data.message ?? "An unknown error occurred"
            }
          />
        )}
        {loading ? (
          <SpinnerLegacy />
        ) : (
          <OrderPaymentMethodFormFragment
            paymentType={paymentMethod}
            stripeAccountId={stripeAccountId}
            buyerName={buyerName}
            onChange={(e) =>
              setPaymentMethod(e.currentTarget.value as "stripe" | "manual")
            }
            isProforma={true}
            retailerPreference={paymentType}
          />
        )}
      </DialogContent>
      <DialogCTA>
        <Button loading={loading} onClick={requestCardPayment}>
          Confirm
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
